import {
  PostExportKitBodyDto,
  groupKitControllerExportKitBundle,
  groupKitDocumentControllerExportDocument,
} from "@/lib/gen-api";
import { toast } from "sonner";
import { useGroupUuid } from "../hooks/use-group-uuid";
import { useKitUuid } from "../hooks/use-kit-uuid";
import { saveAs } from "../lib/file";

export type ExportFormatType = PostExportKitBodyDto["format"];

export function useExportAction() {
  const groupUuid = useGroupUuid();
  const kitUuidFromHook = useKitUuid();

  return {
    exportDocument: async (
      format: ExportFormatType,
      documentUuid: string,
      filename: string | undefined,
    ) => {
      if (!groupUuid || !kitUuidFromHook) {
        throw new Error("Group or kit UUID not found");
      }

      const response = await groupKitDocumentControllerExportDocument({
        path: {
          groupUuid,
          kitUuid: kitUuidFromHook,
          documentUuid,
        },
        body: {
          format,
        },
      });

      if (!response.data || response.data.length === 0) {
        toast.error("Failed to export document. Please try again later.");
        return;
      }

      saveAs(
        response.data as unknown as Buffer,
        (filename || "idea") + "." + format,
      );
    },

    exportKitBundle: async (
      filename: string | undefined,
      body: PostExportKitBodyDto,
      kitUuid?: string,
    ) => {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      const selectedKitUuid = kitUuid || kitUuidFromHook;

      if (!selectedKitUuid) {
        throw new Error("Kit UUID is required");
      }

      const response = await groupKitControllerExportKitBundle({
        path: {
          groupUuid,
          kitUuid: selectedKitUuid,
        },
        body: body,
      });

      if (!response.data) {
        toast.error("Failed to export documents. Please try again later.");
        return;
      }

      const signedUrl = response.data.signedUrl;
      const fileResponse = await fetch(signedUrl);
      const fileContents = await fileResponse.blob();
      saveAs(fileContents as unknown as Buffer, (filename || "kit") + ".zip");
    },
  };
}
